import { getImageProps } from "next/image";

/**
 * Detects image URLs already optimized by Cloudflare Image Resizing at the
 * Magento origin (path rewritten under `/images/` on an `m24-` Magento host).
 * Such assets must skip `next/image` (`/_next/image`) to avoid a redundant
 * second optimization pass.
 */
const isOptimizedMagentoImageUrl = (src: string): boolean => {
  if (!src.startsWith("http")) {
    return false;
  }

  try {
    const { pathname, hostname } = new URL(src);
    return pathname.startsWith("/images/") && hostname.startsWith("m24-");
  } catch {
    return false;
  }
};

export const getOptimizedBackgroundImage = (src: string, width?: number, height?: number) => {
  const isOptimizableSource = src.startsWith("/") || src.startsWith("http");
  const hasFixedDimensions = typeof width === "number" && typeof height === "number";

  // Skip /_next/image for assets already optimized at the Magento origin so
  // they are not routed through a redundant second optimization pass.
  if (!isOptimizableSource || isOptimizedMagentoImageUrl(src)) {
    return `url('${src}')`;
  }

  try {
    const imageConfig = hasFixedDimensions
      ? { alt: "", src, width, height }
      : { alt: "", src, fill: true, sizes: "100vw" };

    const {
      props: { srcSet, src: optimizedSrc },
    } = getImageProps(imageConfig);

    if (!srcSet || !hasFixedDimensions) {
      return `url('${optimizedSrc || src}')`;
    }

    const imageSet = srcSet
      .split(",")
      .map((entry) => {
        const [url, descriptor] = entry.trim().split(" ");
        return `url('${url}') ${descriptor}`;
      })
      .join(", ");

    return `image-set(${imageSet})`;
  } catch {
    return `url('${src}')`;
  }
};
